import { createGlobalStyle } from 'styled-components';

export const theme = {
  colors: {
    primary: '#455B6B', // Deeper, more saturated blue-gray
    secondary: '#9CB676', // More vibrant green
    secondaryLight: '#B4CC8F', // Lighter version of vibrant green
    accent: '#B5C4D3', // More saturated grayish blue
    accentLight: '#C9D6E2', // Lighter version of saturated grayish blue
    light: '#F9F9F7', // Off-white (unchanged)
    dark: '#324550', // Darker version of primary
    text: '#263238', // Even darker for text
    textLight: '#6A7F8C', // Lighter text color based on primary
    bgLight: '#F9F9F7', // Off-white background (unchanged)
    bgDark: '#455B6B', // Matches primary for consistency
    overlay: 'rgba(69, 91, 107, 0.08)', // Overlay based on primary

    // Pastel colors adjusted to complement the new palette
    lavender: '#B5C4D3', // Using accent color
    sage: '#9CB676', // Using secondary color
    blush: '#E7C8C8', // More saturated pink that complements the palette
    sky: '#A7C1D6', // More saturated blue that works with the palette
    mint: '#B4CC8F', // Using secondary-light
    peach: '#E7C8B0', // More saturated peach that complements the palette
  },
  fonts: {
    heading: "'Playfair Display', serif",
    body: "'Raleway', sans-serif",
    accent: "'Cormorant Garamond', serif", // Elegant accent font
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '2rem',
    lg: '4rem',
    xl: '8rem',
  },
  breakpoints: {
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
  shadows: {
    small: '0 2px 8px rgba(69, 91, 107, 0.05)',
    medium: '0 4px 12px rgba(69, 91, 107, 0.08)',
    large: '0 8px 24px rgba(69, 91, 107, 0.12)',
    glow: '0 5px 15px rgba(156, 182, 118, 0.15)',
    pastelSm: '0 2px 10px rgba(181, 196, 211, 0.2)',
    pastelMd: '0 4px 15px rgba(181, 196, 211, 0.25)',
    pastelLg: '0 8px 30px rgba(181, 196, 211, 0.3)',
  },
  transitions: {
    fast: '0.2s ease',
    medium: '0.3s ease',
    slow: '0.5s ease',
  },
};

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: ${(props) => props.theme.fonts.body};
    font-size: 16px;
    line-height: 1.7;
    color: ${(props) => props.theme.colors.text};
    background-color: ${(props) => props.theme.colors.bgLight};
    letter-spacing: 0.01em;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${(props) => props.theme.fonts.heading};
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: ${(props) => props.theme.spacing.sm};
    color: ${(props) => props.theme.colors.primary};
    letter-spacing: 0.03em;
  }

  h1 {
    font-size: 3rem;
    font-weight: 700;
  }

  h2 {
    font-size: 2.5rem;
  }

  h3 {
    font-size: 2rem;
  }

  p {
    margin-bottom: ${(props) => props.theme.spacing.sm};
  }

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
    transition: all ${(props) => props.theme.transitions.medium};
    
    &:hover {
      color: ${(props) => props.theme.colors.secondary};
    }
  }

  ul {
    list-style: none;
  }

  .container {
    width: 90%;
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 ${(props) => props.theme.spacing.sm};
  }

  section {
    padding: ${(props) => props.theme.spacing.lg} 0;
  }

  .btn {
    display: inline-block;
    padding: 14px 32px;
    border-radius: 0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    transition: all ${(props) => props.theme.transitions.medium};
    cursor: pointer;
    font-size: 0.9rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 0;
      background-color: rgba(255, 255, 255, 0.1);
      transition: height ${(props) => props.theme.transitions.medium};
      z-index: -1;
    }
    
    &:hover::after {
      height: 100%;
    }
  }

  input, textarea, select {
    font-family: ${(props) => props.theme.fonts.body};
    font-size: 1rem;
    padding: 12px 15px;
    border: 1px solid ${(props) => props.theme.colors.accent};
    border-radius: 0;
    transition: border-color ${(props) => props.theme.transitions.fast};
    
    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.secondary};
      box-shadow: ${(props) => props.theme.shadows.small};
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    h1 {
      font-size: 2.5rem;
    }

    h2 {
      font-size: 2rem;
    }

    h3 {
      font-size: 1.7rem;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    section {
      padding: ${(props) => props.theme.spacing.md} 0;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    body {
      font-size: 15px;
    }
  }
  
  ::selection {
    background-color: ${(props) => props.theme.colors.secondary};
    color: ${(props) => props.theme.colors.dark};
  }
`;
